import { memo, ReactNode } from 'react'
import {
  MRT_Cell,
  MRT_Column,
  MRT_Row,
  MRT_TableInstance,
} from 'material-react-table'
import { Box, Skeleton } from '@mui/material'
import { DocumentRow } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import { getCheckboxFormattedValue } from '@/utils/data-type'
import { getFormattedDateTimeString } from '@/utils/time'
import { isTimeFieldType } from '@/utils/typescript'
import { useBulkEditValueContext } from './BulkEditValueProvider'
import PickerCell from './PickerCell'

type FieldCellProps = {
  field: ProjectGridField
  cell: MRT_Cell<Required<DocumentRow>>
  column: MRT_Column<Required<DocumentRow>>
  row: MRT_Row<Required<DocumentRow>>
  table: MRT_TableInstance<Required<DocumentRow>>
  isLite?: boolean
}

function FieldCell({
  field,
  cell,
  column,
  row,
  table,
  isLite = false,
}: FieldCellProps) {
  // Check if this cell is pending a bulk edit result
  const { isCellPending } = useBulkEditValueContext()
  if (isCellPending?.(row.original.id, field.id)) {
    return <Skeleton variant="text" width="60%" animation="pulse" />
  }

  let value: ReactNode = cell.getValue() as string
  const typeCode = field.project_grid_field_type.code
  if (!isLite && typeCode === 'picker') {
    return <PickerCell value={value} table={table} row={row} column={column} />
  }

  if (!value) return null

  const typeCodeIsTimeRelated = isTimeFieldType(typeCode)
  if (typeCode === 'checkbox') {
    value = getCheckboxFormattedValue(value as string)
  } else {
    if (typeCode === 'currency') {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',

        // These options are needed to round to whole numbers if that's what you want.
        //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
        //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
      })
      value = (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          {formatter.format(+value)}
        </Box>
      )
    } else if (typeCodeIsTimeRelated) {
      // Format any date or time values so they are readable (they are stored in ISO format)
      value = getFormattedDateTimeString(value as string, typeCode)
    }
  }

  return <>{value}</>
}

export default memo(FieldCell)
